import { Fragment, useState } from 'react';
import {
  fetchSupplierMapsByRpmAcctSupplierId,
  fetchSupplierMapsBySFDCSupplierId,
  getSupplierMapsByRpmAcctSupplierIdLoadingSubject,
  getSupplierMapsByRpmAcctSupplierIdSubject,
  getSupplierMapsBySFDCSupplierIdLoadingSubject,
  getSupplierMapsBySFDCSupplierIdSubject,
  SupplierMap,
  updateSupplierMap
} from '../../../../../store/SupplierMap';
import { DetailPanelParams } from '../../../../Compounds/DetailPanel/DetailPanel';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle';
import { DetailPanelContainer } from '../../../../Compounds/DetailPanel/DetailPanelContainer';
import { EditFieldType } from '../../../../Compounds/DetailPanel/LabelValueField';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { MapMiniTable } from '../../../../Compounds/Mapping/MiniTable/MapMiniTable';
import { sfdcTableCols } from './SFDCColumnDef';
import { rpmTableCols } from './RPMColumnDef';

export interface SupplierMapDetailsParams {
  data: SupplierMap;
}

export const sMapSections = (data: SupplierMap): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: {
      salesforce_supplier_name: 'Salesforce Supplier',
      supplier_sfid: 'Salesforce ID',
      rpm_supplier_name: 'RPM Supplier',
      rpm_supplier_id: 'RPM ID'
    },
    order: ['salesforce_supplier_name', 'rpm_supplier_name', 'supplier_sfid', 'rpm_supplier_id'],
    links: [
      {
        field: 'salesforce_supplier_name',
        link: null,
        externalLink: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${data.supplier_sfid}/view`,
        external: true
      },
      {
        field: 'rpm_supplier_name',
        link: null,
        externalLink: `${process.env.REACT_APP_RPM_URL}/rpm/Page/Supplier.aspx?Item=${data.rpm_supplier_id}`,
        external: true
      }
    ]
  },
  {
    data,
    canEdit: [],
    fieldMap: {
      createdByDisplay: 'Added By',
      createdAt: 'Added Date'
    },
    order: ['createdByDisplay', 'createdAt']
  },
  {
    data,
    canEdit: [{ field: 'note', type: EditFieldType.TEXTAREA }],
    fieldMap: {
      note: 'Note'
    },
    order: ['note']
  }
];

export const SupplierMapDetails = ({ data }: SupplierMapDetailsParams) => {
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [presetString, setPresetString] = useState<string>('');
  const [mapFetchId, setMapFetchId] = useState<string>('');
  const [mapFetchFunc, setMapFetchFunc] = useState<(id: string) => Promise<SupplierMap[] | undefined>>();
  const [preFill, setPreFill] = useState<'SFDC' | 'RPM' | ''>('');

  const handleSave = (update: SupplierMap) => updateSupplierMap(update);

  const handleNewMap = (source: 'SFDC' | 'RPM') => {
    setPresetString(source === 'SFDC' ? data.salesforce_supplier_name : data.rpm_supplier_name);
    setPreFill(source);
    setIsMapFormOpen(true);
    setMapFetchFunc(
      source === 'SFDC' ? () => fetchSupplierMapsBySFDCSupplierId : () => fetchSupplierMapsByRpmAcctSupplierId
    );
    setMapFetchId(source === 'SFDC' ? data.supplier_id : data.rpm_supplier_id);
  };

  const cardActions = (source: 'SFDC' | 'RPM') => {
    return (
      <button
        className="flex items-center space-x-2"
        onClick={() => handleNewMap(source)}
        data-cy="new-supplier-map-button"
        data-testid="new-supplier-map-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Supplier Map
        </TextSpan>
      </button>
    );
  };

  return (
    <Fragment>
      <div
        className="space-y-4"
        data-testid="supplier-map-details-drawer">
        <DetailViewCard
          enableCollapse
          key="supplier-map-detail-card"
          title="Info">
          <div
            data-testid="supplier-map-detail-card"
            className="min-h-[15rem]">
            <DetailPanelContainer
              data={sMapSections(data)}
              saveAction={(d) => handleSave(d as SupplierMap)}
            />
          </div>
        </DetailViewCard>
        <DetailViewCard
          cardActions={cardActions('SFDC')}
          enableCollapse
          key="rpm_supplier_table"
          title="Also Mapped to Salesforce">
          <MapMiniTable
            tableCols={sfdcTableCols}
            id={data.supplier_id || ''}
            exclude={[data.id || '']}
            fetchFunction={fetchSupplierMapsBySFDCSupplierId}
            dataSubject={getSupplierMapsBySFDCSupplierIdSubject}
            loadingSubject={getSupplierMapsBySFDCSupplierIdLoadingSubject}
          />
        </DetailViewCard>
        <DetailViewCard
          cardActions={cardActions('RPM')}
          enableCollapse
          key="SFDC_supplier_table"
          title="Also Mapped to RPM Supplier">
          <MapMiniTable
            tableCols={rpmTableCols}
            id={data.rpm_supplier_id || ''}
            exclude={[data.id || '']}
            fetchFunction={fetchSupplierMapsByRpmAcctSupplierId}
            dataSubject={getSupplierMapsByRpmAcctSupplierIdSubject}
            loadingSubject={getSupplierMapsByRpmAcctSupplierIdLoadingSubject}
          />
        </DetailViewCard>
      </div>
      <NewMapDialog
        open={isMapFormOpen}
        setOpen={setIsMapFormOpen}
        mapType={EntityType.SUPPLIER}
        inputString={presetString}
        preFill={preFill}
        fetch={mapFetchFunc}
        id={mapFetchId}
      />
    </Fragment>
  );
};
